import {Observable}                               from 'rxjs';
import {
  ApDynformsConfigFieldset
}                                                 from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {distinctUntilChanged, filter, map}        from 'rxjs/operators';
import {
  ApDynformsConfigNumerictextbox
}                                                 from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {ApDynformsValidator}                      from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                               from '@angular/forms';
import {
  ApDynformsConfigComboBox
}                                                 from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {
  ApDynformsConfigPlaceholder
}                                                 from '../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {Injectable}                               from '@angular/core';
import {GetRoundNumericPipe}                      from '../../../ap-utils';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridGroupColumnConfig
}                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {NFertilizationCommonEntryService}         from '../services/n-fertilization-common-entry.service';
import {NAppSettingsConfigBase}                   from './base/n-app-settings-config.base';
import {
  NFertilizationPlantProtectionService
}                                                 from '../../../services/data/n-fertilization-plant-protection.service';
import {INFertilizationPlanFieldToSelectGridItem} from '../types/n-fertilization-planning-entry.types';
import {
  FieldScanData,
  GetGrowthStageData
}                                                 from '../../../types/n-fertilization-plant-protection.types';
import {ApTranslationService}                     from '../../../ap-utils/service/ap-translation.service';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;
import IApplicationSettingsParameters = Data.Agronomic.IApplicationSettingsParameters;

@Injectable()
export class ApplicationMapAbsoluteConfig extends NAppSettingsConfigBase {
  private _minAllowed = 0;
  private _maxAllowed = 260;

  constructor(private translateService: ApTranslationService,
              private nFertilizationCommonEntryService: NFertilizationCommonEntryService,
              private nFertilizationPlantProtectionService: NFertilizationPlantProtectionService,
              private roundNumericPipe: GetRoundNumericPipe) {
    super();
  }

  public formBuilder(values: any, formValues$: Observable<any>, cropKey: number | undefined, avgNMin: number | undefined,
                     applicationSettings: IApplicationSettings, isModalField: boolean, applyBlinkEffectOnInit: boolean,
                     diagramTemplateRef?: any): ApDynformsConfigFieldset[] {
    const applyBlinkEffect = !(isModalField || !applyBlinkEffectOnInit);
    const parameters = applicationSettings.Parameter;
    const min$ = formValues$.pipe(
      filter(x => Number.isFinite(x['Minimum']) && x['Minimum'] >= 0),
      map((v) => v['Minimum']),
      distinctUntilChanged()
    );
    const max$ = formValues$.pipe(
      filter(x => Number.isFinite(x['Maximum']) && x['Maximum'] >= 0),
      map((v) => v['Maximum']),
      distinctUntilChanged()
    );
    const targetRate = this.translateService.translate('Calculated_Target_Rate');
    const unitRate = this.translateService.translate('Unit_Rate');
    const weightedAverageTextInfoTranslationKey = parameters?.deadBioMass != null
      ? 'Information_Calculated_Target_Rate'
      : 'Information_Calculated_Target_Rate_Without_DeadBioMass';
    return [
      new ApDynformsConfigFieldset({
        key: 'Agronomy',
        legend: 'Global__Agronomy',
        before: diagramTemplateRef,
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'Threshold',
            label: 'Global__Threshold_kg_NUptake_per_ha',
            value: values['Threshold'],
            value$: formValues$.pipe(
              filter(() => !isModalField),
              filter(formValues => Number.isFinite(formValues['ECStage'])),
              distinctUntilChanged((prev, curr) => prev['ECStage'] === curr['ECStage']),
              map(formValues => this.nFertilizationCommonEntryService.getThreshold(formValues['ECStage'], cropKey, avgNMin))
            ),
            min: 0,
            max: 100,
            step: 1,
            decimals: 0,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'NUptakeAreaWeightedAverage',
            label: 'AvgNUptakeShort',
            infoText: 'NUptakeAreaWeightedAverageInfoText',
            value: values['NUptakeAreaWeightedAverage'],
            format: 'n0',
            informational: true
          }),
          this.generateSlopeControls(parameters, values, applyBlinkEffect),
          parameters?.deadBioMass != null
            ? new ApDynformsConfigComboBox({
              key: 'DeadBioMass',
              label: 'Global__ExtinctBiomass',
              value: values['DeadBioMass'],
              options: [
                {value: 0, text: '0%'},
                {value: 10, text: '10%'},
                {value: 20, text: '20%'},
                {value: 30, text: '30%'},
                {value: 40, text: '40%'},
                {value: 50, text: '50%'},
                {value: 60, text: '60%'}
              ],
              textField: 'text',
              valueField: 'value',
              blinkOnInit: applyBlinkEffect,
              validators: [
                new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
              ]
            })
            : new ApDynformsConfigPlaceholder()
        ]
      }),
      new ApDynformsConfigFieldset({
        legend: 'Nutrients_Pages_Popups__Amount',
        key: 'Fieldset_Amount',
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'Amount',
            label: 'N__SetPoint_kg_N_per_ha',
            infoText: 'Infotext_nominal_n_uptake',
            value: values['Amount'],
            min: this._minAllowed,
            max: this._maxAllowed,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: ''}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'AreaWeightedAverage',
            label: `${targetRate} ${unitRate}`,
            value: values['NUptakeAreaWeightedAverage'],
            value$: formValues$.pipe(
              map(formValues => this._targetRateFormula(formValues['Amount'], formValues['ControlFactor'],
                formValues['DeadBioMass'], values['NUptakeAreaWeightedAverage'], parameters))
            ),
            infoText: weightedAverageTextInfoTranslationKey,
            format: 'n0',
            disabled: true,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: ''})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Constant',
            label: 'Nutrients__Constant_Amount_kg_N_per_ha',
            value: values['Constant'],
            min: min$,
            max: max$,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: ''})
            ]
          }),
          new ApDynformsConfigPlaceholder(),
          new ApDynformsConfigNumerictextbox({
            key: 'Minimum',
            label: 'Nutrients__Minimum_kg_n_per_ha',
            value: values['Minimum'],
            value$: min$,
            min: this._minAllowed,
            max: max$,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: ''})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Maximum',
            label: 'Nutrients__Maximum_kg_n_per_ha',
            value: values['Maximum'],
            value$: max$,
            min: min$,
            max: this._maxAllowed,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: ''})
            ]
          })
        ]
      })
    ];
  }

  public gridBuilderConfig(modeMode$: Observable<boolean>, appSettingsParameters: IApplicationSettingsParameters = null): ApDynGridColumnConfigBase[] {
    const applicationGroupColumns = [];
    applicationGroupColumns.push(
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Average_Amount',
        field: 'Amount',
        width: 50,
        columnLoading: 'ColumnLoading',
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        tooltip: (dataItem) => dataItem['ErrorMessage'],
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Threshold',
        field: 'Threshold',
        width: 50,
        columnLoading: 'ColumnLoading',
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        tooltip: (dataItem) => dataItem['ErrorMessage']
      })
    );

    if (appSettingsParameters == null || (appSettingsParameters.slopeShown && appSettingsParameters.slopeType === 'numeric')) {
      applicationGroupColumns.push(
        new ApDynGridPropertyColumnConfig({
          title: 'N__RateSlope',
          field: 'ControlFactor',
          width: 50,
          columnLoading: 'ColumnLoading',
          filterType: FilterType.NUMBER,
          cssClass: 'right',
          filterable: true,
          tooltip: (dataItem) => dataItem['ErrorMessage']
        }),
      );
    } else if (appSettingsParameters.slopeShown && appSettingsParameters.slopeType === 'dropDown') {
      applicationGroupColumns.push(
        new ApDynGridPropertyColumnConfig({
          title: 'N__RateSlope',
          field: 'ControlFactorDescription',
          width: 50,
          columnLoading: 'ColumnLoading',
          filterable: true,
          tooltip: (dataItem) => dataItem['ErrorMessage']
        })
      );
    }

    applicationGroupColumns.push(
      new ApDynGridPropertyColumnConfig({
        title: 'Global__RateConst',
        field: 'Constant',
        hide: true,
        width: 50,
        columnLoading: 'ColumnLoading',
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        tooltip: (dataItem) => dataItem['ErrorMessage'],
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Min',
        field: 'Minimum',
        hide: true,
        width: 50,
        columnLoading: 'ColumnLoading',
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        tooltip: (dataItem) => dataItem['ErrorMessage'],
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Max',
        field: 'Maximum',
        hide: true,
        width: 50,
        columnLoading: 'ColumnLoading',
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        tooltip: (dataItem) => dataItem['ErrorMessage'],
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      })
    );

    const applicationHalfViewGroupColumns = [];
    applicationHalfViewGroupColumns.push(
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Threshold',
        field: 'Threshold',
        width: 50,
        hide: new ApDynGridColumnHideConfig({
          selfHide: modeMode$
        }),

        columnLoading: 'ColumnLoading',
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        tooltip: (dataItem) => dataItem['ErrorMessage']
      }),
    );
    if (appSettingsParameters == null || (appSettingsParameters.slopeShown && appSettingsParameters.slopeType === 'numeric')) {
      applicationHalfViewGroupColumns.push(
        new ApDynGridPropertyColumnConfig({
          title: 'N__RateSlope',
          field: 'ControlFactor',
          width: 50,
          columnLoading: 'ColumnLoading',
          hide: new ApDynGridColumnHideConfig({
            selfHide: modeMode$
          }),
          filterType: FilterType.NUMBER,
          cssClass: 'right',
          filterable: true,
          tooltip: (dataItem) => dataItem['ErrorMessage']
        })
      );
    } else if (appSettingsParameters.slopeShown && appSettingsParameters.slopeType === 'dropDown') {
      applicationHalfViewGroupColumns.push(
        new ApDynGridPropertyColumnConfig({
          title: 'N__RateSlope',
          field: 'ControlFactorDescription',
          hide: new ApDynGridColumnHideConfig({
            selfHide: modeMode$
          }),
          width: 50,
          columnLoading: 'ColumnLoading',
          filterable: true,
          tooltip: (dataItem) => dataItem['ErrorMessage']
        })
      );
    }
    const configNameTranslation = this.translateService.translate('Global__AppFile');
    const unitTranslation = this.translateService.translate('Nutrients__kg_N_per_ha');
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'AvgNUptakeShort',
        field: 'AvgNUptake',
        headerExtraLines: [
          'Nutrients__kg_N_per_ha'
        ],
        width: 80,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: `${configNameTranslation} ${unitTranslation}`,
        hide: true,
        groupColumns: applicationGroupColumns
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Average_Amount',
        field: 'Amount',
        width: 50,
        hide: new ApDynGridColumnHideConfig({
          selfHide: modeMode$
        }),
        columnLoading: 'ColumnLoading',
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        filterable: true,
        tooltip: (dataItem) => dataItem['ErrorMessage'],
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        groupColumns: applicationHalfViewGroupColumns
      })
    ];
  }

  public updateFormValuesByParameters(values: any, selectedItems: INFertilizationPlanFieldToSelectGridItem[], cropKey: number | undefined,
                                      avgNMin: number | undefined, applicationSettings: IApplicationSettings | undefined): void {
    const parameters = applicationSettings.Parameter;
    const fieldsScanData = selectedItems.map(x => ({FieldStatus: x.Status, ScanData: x.ScanData} as FieldScanData));
    const scansByApplicationSettings = this.nFertilizationPlantProtectionService.getValidBestScansByApplicationSettings(
      fieldsScanData, applicationSettings);
    const growthStages = this.nFertilizationPlantProtectionService.getGrowStages(
      scansByApplicationSettings.map(x => ({ScanData: x} as GetGrowthStageData))
    );
    const growthStageMin = growthStages.Min();
    if (!Number.isFinite(values['Threshold'])) {
      values['Threshold'] = this.nFertilizationCommonEntryService.getThreshold(growthStageMin, cropKey, avgNMin);
    }
    if (!Number.isFinite(values['DeadBioMass']) && Number.isFinite(parameters?.deadBioMass)) {
      values['DeadBioMass'] = parameters.deadBioMass;
    } else {
      values['DeadBioMass'] = 0;
    }
    if (!Number.isFinite(values['Amount'])) {
      values['Amount'] = parameters?.recommendation ?? parameters?.targetValue;
    }
    if (!Number.isFinite(values['Constant'])) {
      values['Constant'] = parameters?.uniform;
    }
    if (!Number.isFinite(values['Minimum'])) {
      values['Minimum'] = parameters?.min ?? this._minAllowed;
    }
    if (!Number.isFinite(values['Maximum'])) {
      values['Maximum'] = parameters?.max ?? this._maxAllowed;
    }
    if (!Number.isFinite(values['ControlFactor'])) {
      values['ControlFactor'] = parameters?.slope?.FirstOrDefault()?.Slope ?? 1;
    }
    values['NUptakeAreaWeightedAverage'] = this.nFertilizationPlantProtectionService.calculateAreaWeightedAverageOfScans(
      selectedItems, applicationSettings
    );
  }

  private _targetRateFormula(amount: number | undefined, controlFactor: number | undefined, deadBioMass: number | undefined,
                             areaWeightedAverage: number, parameters: IApplicationSettingsParameters): number {
    if (!amount) {
      return 0;
    }
    let calculationResult = amount;
    if (deadBioMass > 0) {
      const deadBioMassDefining = deadBioMass ?? parameters.deadBioMass;
      const deadBioMassResult = Number.isFinite(deadBioMassDefining) ? deadBioMass / 2 : 1;
      calculationResult = calculationResult + deadBioMassResult;
    }
    const controlFactorDefining = controlFactor ?? parameters.slope?.FirstOrDefault()?.Slope;
    const controlFactorResult = Number.isFinite(controlFactorDefining) ? controlFactorDefining : 1;
    calculationResult = calculationResult - (areaWeightedAverage * controlFactorResult);
    return calculationResult > 0 ? calculationResult : 0;
  }
}
